import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Container } from "./styles";
import "react-data-table-component-extensions/dist/index.css";
import { useAuth } from "../../hooks/useAuth";
import Button from "@mui/material/Button";
import { FormContainer, ButtonContainer } from "./styles";
import { TextField, Typography, MenuItem, Autocomplete } from "@mui/material";
import * as MechanicsService from "../../services/mechanics";
import * as StatesService from "../../services/states";
import { Place } from "@mui/icons-material";

const EstablishmentData = () => {
    const { loggedMechanic, setLoggedMechanic } = useAuth();
    const [inputs, setInputs] = useState({
        name: "",
        address: "",
        neighborhood: "",
        cep: "",
        state: "",
        city: "",
        phone: "",
    });
    const [isUpdating, setIsUpdating] = useState(false);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);

    // Load states on mount
    useEffect(() => {
        async function fetchStates() {
            try {
                const response = await StatesService.getStates();
                setStates(response);
            } catch (error) {
                console.error("Error fetching states:", error);
            }
        }
        fetchStates();
    }, []);

    // Load mechanic data and set initial values
    useEffect(() => {
        if (loggedMechanic) {
            setInputs({
                name: loggedMechanic.name || "",
                address: loggedMechanic.address || "",
                neighborhood: loggedMechanic.neighborhood || "",
                cep: loggedMechanic.cep || "",
                state: loggedMechanic.state || "",
                city: loggedMechanic.city || "",
                phone: loggedMechanic.phone || "",
            });

            // Find and set the initial state
            if (loggedMechanic.state && states.length > 0) {
                const stateObj = states.find(s => s.sigla === loggedMechanic.state);
                if (stateObj) {
                    setSelectedState(stateObj);
                }
            }
        }
    }, [loggedMechanic, states]);

    // Load cities when state changes
    useEffect(() => {
        async function fetchCities() {
            if (selectedState?.sigla) {
                try {
                    const response = await StatesService.getCities(selectedState.sigla);
                    setCities(response);
                    
                    // Find and set the initial city
                    if (inputs.city) {
                        const cityObj = response.find(c => c.nome === inputs.city);
                        if (cityObj) {
                            setSelectedCity(cityObj);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching cities:", error);
                }
            } else {
                setCities([]);
                setSelectedCity(null);
            }
        }
        fetchCities();
    }, [selectedState, inputs.city]);

    const handleInputsChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setInputs((values) => ({ ...values, [name]: value }));
    };

    const handleStateChange = (_, newValue) => {
        setSelectedState(newValue);
        setInputs(prev => ({ ...prev, state: newValue?.sigla || "" }));
        setSelectedCity(null);
        setInputs(prev => ({ ...prev, city: "" }));
    };

    const handleCityChange = (_, newValue) => {
        setSelectedCity(newValue);
        setInputs(prev => ({ ...prev, city: newValue?.nome || "" }));
    };

    const handleSaveButtonChange = async (event) => {
        event.preventDefault();
        if (isUpdating) {
            await handleUpdateMechanic(event);
            setIsUpdating(!isUpdating);
        } else {
            setIsUpdating(!isUpdating);
        }
    };

    const handleUpdateMechanic = async (event) => {
        event.preventDefault();
        const body = {
            name: inputs.name,
            address: inputs.address,
            neighborhood: inputs.neighborhood,
            cep: inputs.cep,
            state: inputs.state,
            city: inputs.city,
            phone: inputs.phone,
        };

        try {
            const updatedMechanic = await MechanicsService.updateMechanic(loggedMechanic.id, body);
            setLoggedMechanic(updatedMechanic);
            alert(`Dados atualizados com sucesso!`);
        } catch (error) {
            alert(`Erro ao atualizar seus dados!`);
        }
    };

    return (
        <Container>
            <Box
                mb={3}
                fontWeight="bold"
                color="#666666"
                fontSize={{
                    xs: "h7.fontSize",
                    sm: "h6.fontSize",
                    md: "h5.fontSize",
                }}
            >
                Configurações do Estabelecimento
            </Box>
            <form onSubmit={handleSaveButtonChange}>
                <FormContainer>
                    <Box display="flex" flexDirection="row">
                        <Place sx={{ fontSize: 35, color: "#666666" }} />
                        <Typography variant="h5" color="#666666" style={{ width: "100%", marginLeft: "10px", marginTop: "2px" }}>
                            <b>{inputs.name}</b>
                        </Typography>
                    </Box>
                    <Typography variant="h6" color="#666666" style={{ width: "100%", marginBottom: "5px" }}>
                        Dados do Estabelecimento:
                    </Typography>
                    <TextField
                        value={inputs.name}
                        name="name"
                        required
                        label="Nome do Estabelecimento"
                        sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                        InputProps={{ readOnly: !isUpdating }}
                        variant="outlined"
                        style={{ width: "100%" }}
                        onChange={handleInputsChange}
                    />
                    <TextField
                        value={inputs.address}
                        name="address"
                        required
                        label="Endereço"
                        sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                        InputProps={{ readOnly: !isUpdating }}
                        variant="outlined"
                        style={{ width: "100%" }}
                        onChange={handleInputsChange}
                    />
                    <TextField
                        value={inputs.neighborhood}
                        name="neighborhood"
                        label="Bairro"
                        sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                        InputProps={{ readOnly: !isUpdating }}
                        variant="outlined"
                        style={{ width: "100%" }}
                        onChange={handleInputsChange}
                    />
                    <TextField
                        value={inputs.cep}
                        name="cep"
                        label="CEP"
                        sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                        InputProps={{ readOnly: !isUpdating }}
                        variant="outlined"
                        style={{ width: "100%" }}
                        onChange={handleInputsChange}
                    />
                    <TextField
                        value={inputs.phone}
                        name="phone"
                        label="WhatsApp / Telefone"
                        sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                        InputProps={{ readOnly: !isUpdating }}
                        variant="outlined"
                        style={{ width: "100%" }}
                        onChange={handleInputsChange}
                    />
                    <Autocomplete
                        disablePortal
                        id="state"
                        options={states}
                        getOptionLabel={(option) => `${option.sigla} - ${option.nome}`}
                        isOptionEqualToValue={(option, value) => option.sigla === value?.sigla}
                        value={selectedState}
                        onChange={handleStateChange}
                        disabled={!isUpdating}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                label="Estado"
                                sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                            />
                        )}
                        style={{ width: "100%" }}
                    />
                    <Autocomplete
                        disablePortal
                        id="city"
                        options={cities}
                        getOptionLabel={(option) => option.nome}
                        isOptionEqualToValue={(option, value) => option.nome === value?.nome}
                        value={selectedCity}
                        onChange={handleCityChange}
                        disabled={!isUpdating || !selectedState}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                label="Cidade"
                                sx={{ input: { color: isUpdating ? "black" : "gray" } }}
                            />
                        )}
                        style={{ width: "100%" }}
                    />
                </FormContainer>
                <ButtonContainer>
                    <Box display="flex" justifyContent="flex-end" padding="20px" justifySelf="center">
                        {isUpdating && (
                            <Button variant="outlined" color="error" style={{ marginRight: "4px" }} type="submit">
                                Salvar Alterações
                            </Button>
                        )}
                        <Button variant="contained" color="primary" style={{ marginRight: "4px" }} onClick={() => setIsUpdating(!isUpdating)}>
                            {isUpdating ? "Cancelar" : "Editar Dados"}
                        </Button>
                    </Box>
                </ButtonContainer>
            </form>
        </Container>
    );
};

export default EstablishmentData;
